/**
 * The unified to-do tray's model — pure functions, no React.
 *
 * Every row answers one question, who moves next, and that picks its section:
 * **Needs your yes** (anyone waiting on your decision: Kory's suggestions, a
 * Drive's, a colleague's approval, a failed run, a run waiting for your
 * go-ahead, Kory's reviews), **Your to-dos** (yours to do: manual, meeting and
 * SOP to-dos, accepted cards with nothing to run), **Kory is on it** (live
 * runs), **Since you left** (unread news: update cards, never Accept),
 * **Older suggestions** (suggestions nobody answered in a week, folded) and
 * **Done**. The tray, the brief, the Overview feed and the To-dos page share
 * these sections; the brief's numbers only order rows inside them.
 * Copy here never names a tool, an action slug or a ticket id.
 */

import { format, isValid, parseISO, subDays } from "date-fns";
import { BUSINESS_TZ, combineBusinessDateTime } from "@/lib/local-date";
import { isAutoTodo, todoOriginShort } from "@/lib/todo-taxonomy";
import { resolveTodoRenderKind } from "@/components/agent/view-registry";
import type { DealRankKey, TodoActionDTO, TodoChangeDTO, TodoDTO } from "@/hooks/use-agent-todos";

export type TrayBucket = "needs_you" | "your_todos" | "in_progress" | "since_you_left" | "older" | "history";

export const TRAY_BUCKETS: { key: TrayBucket; label: string }[] = [
  { key: "needs_you", label: "Needs Your Yes" },
  { key: "your_todos", label: "Your To-dos" },
  { key: "in_progress", label: "Kory Is on It" },
  { key: "since_you_left", label: "Since You Left" },
  { key: "older", label: "Older Suggestions" },
  { key: "history", label: "Done" },
];

const LIVE_RUN = new Set(["preparing", "scheduled", "running", "waiting_external", "uncertain"]);

/** A suggestion nobody answered for this long folds into Older suggestions (it never expires). */
export const STALE_SUGGESTION_DAYS = 7;

// The row rules live in the standard to-do model; re-exported so tray callers keep their imports.
export { dueText, isMandated, isProposal, isProps, isStagedSalesStep } from "@/components/todos/todo-line-model";
import { isProposal, isProps, isReplaced } from "@/components/todos/todo-line-model";

/** To-dos where the person reviews something Kory drafted for approval: the
 *  rolling memory, company-fact, draft-standard and draft-SOP reviews. */
const REVIEW_SOURCES = new Set([
  "kory_memory_review",
  "kory_knowledge_review",
  "foundation_standard_review",
  "sop_draft_review",
]);

/** A colleague's request the person decides in its own view. */
const APPROVAL_KINDS = new Set(["time_off_approval", "expense_approval", "kpi_proposal_approval"]);

/** An update card: news (a reply, a merged PR) to read, open or dismiss. Never Accept. */
export function isUpdate(todo: TodoDTO): boolean {
  return todo.envelope?.kind === "update";
}

/** Props waiting on the giver's words (accepted or not), until a run takes over. */
function propsWaiting(todo: TodoDTO): boolean {
  const run = todo.run;
  return isProps(todo) && (!run || run.state === "preparing" || run.state === "awaiting_approval");
}

/** Someone (Kory, a Drive, a colleague) is waiting on this person's decision. */
export function waitsOnYou(todo: TodoDTO): boolean {
  const run = todo.run;
  return (
    isProposal(todo) ||
    propsWaiting(todo) ||
    REVIEW_SOURCES.has(todo.source) ||
    todo.envelope?.kind === "review" ||
    APPROVAL_KINDS.has(resolveTodoRenderKind(todo) ?? "") ||
    !!todo.access ||
    todo.next_action === "allow_access" ||
    todo.next_action === "review_send" ||
    todo.next_action === "approve_hold" ||
    run?.state === "awaiting_approval" ||
    run?.state === "failed" ||
    run?.state === "needs_access"
  );
}

/** Failures and missing Google access lead their section. */
/** Needs you now: its run failed or is missing a permission. */
export function urgent(todo: TodoDTO): boolean {
  return todo.run?.state === "failed" || todo.run?.state === "needs_access" || !!todo.access;
}

/** A suggestion (Kory's or a Drive's) left unanswered for a week. */
export function isStaleSuggestion(todo: TodoDTO, now: Date = new Date()): boolean {
  if (!isProposal(todo) || !todo.created_at) return false;
  const at = new Date(todo.created_at);
  return !isNaN(at.getTime()) && at < subDays(now, STALE_SUGGESTION_DAYS);
}

export function trayBucket(todo: TodoDTO, now?: Date): TrayBucket {
  if (isUpdate(todo)) return todo.envelope?.update?.unread ? "since_you_left" : "history";
  if (todo.completed_at || todo.dismissed_at) return "history";
  if (waitsOnYou(todo)) return isStaleSuggestion(todo, now) ? "older" : "needs_you";
  if (todo.run && LIVE_RUN.has(todo.run.state)) return "in_progress";
  return "your_todos";
}

/** One line in a section: a row, or one Drive's suggestions stacked as one.
 *  ``title`` is the Drive's name when the card carries it, so the stack header
 *  never has to title-case an id. A person-leg slug stack leaves it null. */
export type TrayItem =
  | { kind: "todo"; todo: TodoDTO }
  | { kind: "stack"; key: string; drive: string; title: string | null; todos: TodoDTO[] };

export interface TrayGroupModel {
  key: TrayBucket;
  label: string;
  todos: TodoDTO[];
  /** ``todos`` as the section shows them: a Drive's open suggestions share one stacked line. */
  items: TrayItem[];
}

export function itemKey(item: TrayItem): string {
  return item.kind === "todo" ? item.todo.id : item.key;
}

export function itemTodos(item: TrayItem): TodoDTO[] {
  return item.kind === "todo" ? [item.todo] : item.todos;
}

/** A Drive's suggestion: it stacks with the rest from the same Drive, on ``todo.drive.id``,
 *  and carries the title. One card, and a non-proposal, do not stack. */
function driveOf(todo: TodoDTO): { key: string; title: string | null } | null {
  if (!isProposal(todo)) return null;
  if (todo.drive?.id) {
    const title = todo.drive.title?.trim();
    return { key: todo.drive.id, title: title || null };
  }
  return null;
}

/** Two or more suggestions from one Drive become one stacked line where the first of them sorts. */
export function stackDrives(todos: TodoDTO[]): TrayItem[] {
  const byDrive = new Map<string, TodoDTO[]>();
  const titles = new Map<string, string | null>();
  for (const t of todos) {
    const drive = driveOf(t);
    if (!drive) continue;
    byDrive.set(drive.key, [...(byDrive.get(drive.key) ?? []), t]);
    if (drive.title) titles.set(drive.key, drive.title);
  }
  const placed = new Set<string>();
  return todos.flatMap((t): TrayItem[] => {
    const drive = driveOf(t);
    const stack = drive ? byDrive.get(drive.key)! : null;
    if (!drive || !stack || stack.length < 2) return [{ kind: "todo", todo: t }];
    if (placed.has(drive.key)) return [];
    placed.add(drive.key);
    return [{ kind: "stack", key: `drive:${drive.key}`, drive: drive.key, title: titles.get(drive.key) ?? null, todos: stack }];
  });
}

/** Undated rows sort after dated ones. */
function dueKey(todo: TodoDTO): string {
  return todo.due_at ?? "9999";
}

/** Two Deal Rank keys in order, lowest first: each part in turn. */
function byDealRank(a: DealRankKey, b: DealRankKey): number {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] < b[i] ? -1 : 1;
  }
  return 0;
}

/** Rows on a deal trade places among their own slots into Deal Rank order (``deal_rank``, lowest key first);
 *  every other row keeps its place. */
export function inDealRank(rows: readonly TodoDTO[]): TodoDTO[] {
  const ranked = rows.filter((t) => t.deal_rank != null);
  const ordered = [...ranked].sort((a, b) => byDealRank(a.deal_rank!, b.deal_rank!));
  let next = 0;
  return rows.map((t) => (t.deal_rank != null ? ordered[next++] : t));
}

/** Group rows into the tray's sections, in display order, dropping empty ones.
 *  Inside an open section: Kory's just-proposed cards (``lead``) hold the top,
 *  then failures and missing access, then the brief's order (``order``: its
 *  numbers), then due date (overdue first, undated last), then newest. Rows on
 *  a deal then trade places among their own slots into Deal Rank order.
 *  Since you left and Done are most recent first. */
export function groupTray(
  todos: TodoDTO[],
  {
    includeHistory = false,
    showDone = [],
    pin,
    lead = [],
    order,
    prior,
    now,
  }: {
    includeHistory?: boolean;
    /** Done rows that show even without ``includeHistory``: the ones Kory names. */
    showDone?: readonly string[];
    pin?: ReadonlyMap<string, TrayBucket>;
    lead?: readonly string[];
    order?: ReadonlyMap<string, number>;
    /** Each section's order as last shown: rows already there keep their places, so a card arriving
     *  (three proposals in a few seconds) takes the top of its section and nothing else re-sorts. */
    prior?: ReadonlyMap<TrayBucket, readonly string[]>;
    now?: Date;
  } = {},
): TrayGroupModel[] {
  const byKey = new Map<TrayBucket, TodoDTO[]>();
  const seen = new Set<string>();
  for (const t of todos) {
    if (seen.has(t.id)) continue;
    seen.add(t.id);
    // A row mid-Wave stays where it was until the Wave lands (then FLIPs).
    const b = pin?.get(t.id) ?? trayBucket(t, now);
    if (b === "history" && !includeHistory && !showDone.includes(t.id)) continue;
    byKey.set(b, [...(byKey.get(b) ?? []), t]);
  }
  const leadAt = (t: TodoDTO) => (lead.includes(t.id) ? lead.indexOf(t.id) : Infinity);
  const briefAt = (t: TodoDTO) => order?.get(t.id) ?? Infinity;
  return TRAY_BUCKETS.flatMap(({ key, label }) => {
    const rows = byKey.get(key);
    if (!rows?.length) return [];
    const was = prior?.get(key) ?? [];
    const shownAt = (t: TodoDTO) => was.indexOf(t.id);
    const recent = key === "history" || key === "since_you_left";
    const natural = [...rows].sort((a, b) =>
      recent
        ? (b.completed_at ?? b.dismissed_at ?? "").localeCompare(a.completed_at ?? a.dismissed_at ?? "")
        : leadAt(a) - leadAt(b) ||
          Number(urgent(b)) - Number(urgent(a)) ||
          briefAt(a) - briefAt(b) ||
          dueKey(a).localeCompare(dueKey(b)) ||
          (b.created_at ?? "").localeCompare(a.created_at ?? ""),
    );
    // New to the section first, in the natural order; the rest where they already were (a stable sort).
    const sorted = (recent ? natural : inDealRank(natural)).sort((a, b) => {
      const ai = shownAt(a);
      const bi = shownAt(b);
      if (ai < 0 && bi < 0) return 0;
      return ai < 0 ? -1 : bi < 0 ? 1 : ai - bi;
    });
    return [{ key, label, todos: sorted, items: key === "needs_you" || key === "older" ? stackDrives(sorted) : sorted.map((todo) => ({ kind: "todo" as const, todo })) }];
  });
}

/** A docked tray shows this many lines; See all has the rest. */
export const TRAY_TOP = 4;
/** A phone keeps the page in view: its tray shows the top 3. */
export const PHONE_TRAY_TOP = 3;

/**
 * The tray's first `limit` lines across sections, in order, plus any line holding a row in `keep`
 * (the card Kory names) past them, so it always shows. A section with no line left keeps its
 * header and count, so nothing disappears without a count. Older suggestions only ever show their
 * header; Done stays on the full list, but for a row in `keep` (a card Kory names after it finished).
 * `hidden` is how many rows each section leaves out.
 */
export function topOfTray(
  groups: TrayGroupModel[],
  limit: number,
  keep: readonly string[] = [],
): (TrayGroupModel & { hidden: number })[] {
  let room = limit;
  return groups.flatMap((g) => {
    if (g.key === "history") {
      const kept = g.todos.filter((t) => keep.includes(t.id));
      return kept.length ? [{ ...g, todos: kept, items: kept.map((todo) => ({ kind: "todo" as const, todo })), hidden: 0 }] : [];
    }
    const items = g.items.filter((item) => {
      if (g.key !== "older" && room > 0) {
        room -= 1;
        return true;
      }
      return itemTodos(item).some((t) => keep.includes(t.id));
    });
    const todos = items.flatMap(itemTodos);
    return [{ ...g, todos, items, hidden: g.todos.length - todos.length }];
  });
}

/** The line that is open: the card Kory names, else the brief's current step, else the one you
 *  tapped. Nothing is open at rest. Returns the line's key. */
export function openLine(
  items: readonly TrayItem[],
  { named = [], current = null, tapped = null }: { named?: readonly string[]; current?: string | null; tapped?: string | null },
): string | null {
  const holding = (id: string | null) => (id ? items.find((it) => itemKey(it) === id || itemTodos(it).some((t) => t.id === id)) : undefined);
  for (const id of [...named, current, tapped]) {
    const item = holding(id);
    if (item) return itemKey(item);
  }
  return null;
}

export type StatusTone = "need" | "work" | "done" | "bad" | "muted";

/** The row's one-line state: what it is waiting on, in plain words. */
export function rowStatus(todo: TodoDTO): { text: string; tone: StatusTone } | null {
  const run = todo.run;
  // Waiting on the giver's words; a run in flight or failed says so below instead.
  if (isProps(todo) && !todo.completed_at && !todo.dismissed_at && (!run || run.state === "preparing" || run.state === "awaiting_approval")) {
    return { text: "Worth recognizing · Add your words", tone: "need" };
  }
  if (isProposal(todo)) return { text: needsYourDecision(suggester(todo)), tone: "need" };
  if (isReplaced(todo)) return { text: "Replaced", tone: "muted" };
  if (todo.completed_at) return receiptLine(todo) ? null : { text: "Done", tone: "done" };
  if (todo.dismissed_at) return { text: "Dismissed", tone: "muted" };
  if (todo.next_action === "allow_access") return { text: "Waiting for your OK in Google", tone: "need" };
  if (todo.next_action === "review_send") return { text: "Prepared by Kory · Review draft", tone: "need" };
  if (todo.next_action === "approve_hold") return { text: "Approve a calendar hold", tone: "need" };
  if (run?.state === "awaiting_approval") return { text: "Prepared by Kory · Waiting for your go-ahead", tone: "need" };
  if (run?.state === "failed") return { text: `${failedRunLabel(run.operation?.tool)} · Retry`, tone: "bad" };
  // The row renders the live countdown (HeldSendNotice); this is its plain fallback.
  if (run?.state === "scheduled") return { text: "Sending soon · Undo to stop it", tone: "work" };
  if (run?.state === "uncertain") return { text: "Checking whether it went through", tone: "work" };
  if (run?.engineering) return { text: engineeringLine(run.engineering.phase), tone: "work" };
  if (run?.state === "preparing") return { text: "Kory is preparing it", tone: "work" };
  if (run?.state === "running") return { text: "Kory is working on it", tone: "work" };
  if (run?.state === "waiting_external") return { text: "Waiting on someone else", tone: "work" };
  if (run?.state === "succeeded") return receiptLine(todo) ? null : { text: "Kory finished its part", tone: "done" };
  return null;
}

/** A run waiting on Google: held for an Allow, or refused mid-call for the grant (it re-runs after a reconnect). */
export function waitsOnGoogle(todo: TodoDTO): boolean {
  const run = todo.run;
  return run?.state === "needs_access" || (run?.state === "failed" && !!run.receipt?.connect_url);
}

/** What a failed run did not do: never "on it", never a tool name. */
export function failedRunLabel(tool: unknown): string {
  if (tool === "mail_create_draft" || tool === "mail_update_draft") return "Couldn't save the draft";
  if (tool === "mail_send_draft") return "Couldn't send";
  if (tool === "file_ticket") return "Couldn't file";
  if (tool === "remember") return "Couldn't save";
  return "Didn't go through";
}

/** The saved draft in the person's own Gmail, until it is sent. */
export function draftGmailUrl(run: { receipt: Record<string, unknown> | null } | null | undefined): string | null {
  const receipt = run?.receipt;
  if (!receipt || receipt.sent) return null;
  return typeof receipt.gmail_url === "string" && receipt.gmail_url ? receipt.gmail_url : null;
}

function engineeringLine(phase: string | null): string {
  switch (phase) {
    case "launched":
      return "Kory is working · Build started";
    case "review":
      return "Kory is working · Change ready for review";
    case "merged":
      return "Merged · Deploying";
    case "deployed":
      return "Deployed";
    default:
      return "Kory is working · Preparing a change for review";
  }
}

/** Who suggested the card: the Drive's name, else "a Drive" when only its slug is known, else Kory.
 *  ``driveTitle`` is a name the brief already carries when the to-do itself has none. */
export function suggester(todo: Pick<TodoDTO, "drive" | "source" | "drive_slug">, driveTitle?: string | null): string {
  const title = todo.drive?.title?.trim() || driveTitle?.trim();
  if (title) return title;
  if (todo.drive_slug) return "a Drive";
  return "Kory";
}

/** The suggestion row's state line. The tray and the brief share this sentence. */
export function needsYourDecision(by: string): string {
  return `Suggested by ${by} · Needs your decision`;
}

/** A finished run's receipt, in words (never a raw id). */
export function receiptLine(todo: TodoDTO): string | null {
  const run = todo.run;
  if (run?.state !== "succeeded" || !run.receipt) return null;
  if (isProps(todo)) return "Props posted";
  if (typeof run.receipt.ticket === "string") return `Filed · ${run.receipt.ticket}`;
  if (todo.completion_target === "send") return "Sent";
  if (todo.completion_target === "hold") return "Hold placed on your calendar";
  return null;
}

/** The brief confirmation a card shows when its run just succeeded here: what it became, with the link
 *  when there is one ("Filed HAI-7", "Draft saved", "Hold placed"). */
export function confirmation(todo: TodoDTO): { text: string; url: string | null } {
  const result = todo.result;
  if (!result) return { text: receiptLine(todo) ?? "Done", url: null };
  if (result.kind === "draft") return { text: "Draft saved", url: result.url };
  if (result.kind === "hold") return { text: "Hold placed", url: result.url };
  if (result.kind === "email") return { text: "Sent", url: null };
  return { text: result.label, url: result.url };
}

/** A finished send / hold's receipt line: "Sent · 9:41 AM". Only the time when the title already is the result. */
export function receiptTime(todo: TodoDTO): string | null {
  const run = todo.run;
  const line = receiptLine(todo);
  if (!line) return null;
  if (todo.result) {
    const at = run?.finished_at ? new Date(run.finished_at) : null;
    return at && !isNaN(at.getTime()) ? format(at, "h:mm a") : null;
  }
  if (!run?.finished_at) return line;
  const at = new Date(run.finished_at);
  return isNaN(at.getTime()) ? line : `${line} · ${format(at, "h:mm a")}`;
}

/** Where the row came from, for the meta line. Quiet when the Drive chip already names it. */
export function sourceLabel(todo: TodoDTO): string | null {
  if (todo.drive) return null;
  if (todo.source === "cos") return "From Kory";
  if (todo.drive_slug) return "From a Drive";
  const origin = todoOriginShort(todo);
  return origin ? `From ${origin}` : null;
}

/** A tray row's status, most useful first: what it needs, then where it came from. Due and the owner have
 *  their own places on the row (todo-line-model.ts); the drawer keeps the full detail. */
export function trayMeta(todo: TodoDTO, { status = true } = {}): { text: string; tone: StatusTone }[] {
  const state = status && todo.run?.state !== "scheduled" ? rowStatus(todo) : null;
  // An SOP to-do's chip already names where it came from.
  const source = isAutoTodo(todo) ? null : sourceLabel(todo);
  const muted = (text: string) => ({ text, tone: "muted" as const });
  return [
    ...(state ? [state] : []),
    ...(todo.run?.engineering ? [muted("Linked engineering ticket")] : []),
    ...(source ? [muted(source)] : []),
  ];
}

const MEMORY_SLUGS = new Set(["kory.remember"]);

/** A CoS card whose tap saves a memory or a company fact. */
export function isMemory(todo: TodoDTO): boolean {
  return todo.actions.some((a) => a.kind === "action" && !!a.action_slug && MEMORY_SLUGS.has(a.action_slug));
}

/** Kory files a "File ticket: …" card in Linear on a yes. */
export const TICKET_EXECUTOR = "ticket";

const TICKET_DRAFT = /^\s*file\s+(?:a\s+)?(?:ticket|linear|[a-z]+-\d+)/i;

/** Kory's "File ticket: …" card: Kory cannot file it, so the person does. */
export function isTicketDraft(title: string): boolean {
  return TICKET_DRAFT.test(title);
}

/** The ticket to paste into Linear: the title after its "File …:" lead, then the notes.
 *  The card's text is Kory's one-line why, a blank line, then the ticket notes; the why is not copied. */
export function ticketText(title: string, body?: string | null): string {
  const colon = title.indexOf(":");
  const name = (colon >= 0 ? title.slice(colon + 1) : title.replace(/^\s*file\s+(?:a\s+)?ticket\s*/i, "")).trim();
  const notes = (body ?? "").split(/\n\s*\n/).slice(1).join("\n\n").trim();
  return notes ? `${name}\n\n${notes}` : name;
}

export interface ActionFailure {
  text: string;
  /** Trying the same tap again can help (not when the card is gone or already decided). */
  retry: boolean;
}

/** A refused tap, in plain words. */
export function actionFailure(error: unknown): ActionFailure {
  const { status, message } = (error ?? {}) as { status?: number; message?: string };
  if (status === 404) return { text: "This card is gone. Refresh to see the latest.", retry: false };
  return { text: message?.trim() || "That didn't go through.", retry: status !== 409 };
}

// ── Conversation provenance ─────────────────────────────────────────────

export function isConversational(change: TodoChangeDTO | null | undefined): change is TodoChangeDTO {
  return !!change && (change.changed_via === "chat" || change.changed_via === "voice");
}

const CHANGE_LABELS: Record<string, string> = {
  accepted: "Accepted",
  approved: "Approved",
  edited: "Updated",
  prepared: "Prepared",
  preparing: "Preparing",
  needs_access: "Asked for your OK in Google",
  declined: "Dismissed",
  snoozed: "Snoozed",
  completed: "Completed",
  canceled: "Canceled",
  undo: "Undone",
  send_held: "Sending",
  send_canceled: "Send stopped",
  created: "Added",
  restored: "Restored",
  reopened: "Reopened",
};

/** "Approved" for ``approved``; a readable fallback for anything newer. */
export function changeLabel(change: string): string {
  return CHANGE_LABELS[change] ?? change.replace(/^run_/, "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

/** "Approved · you said “send it”" — the quoted utterance behind a change. */
export function conversationLine(change: TodoChangeDTO): string {
  const said = change.utterance ? ` · you said “${change.utterance}”` : ` by ${change.changed_via}`;
  return `${changeLabel(change.change)}${said}`;
}

/** The prior title an edit recorded, when the edit renamed the to-do. */
export function priorTitle(change: TodoChangeDTO | null | undefined): string | null {
  const was = (change?.detail as { was?: { title?: string | null } } | null | undefined)?.was;
  return change?.change === "edited" && typeof was?.title === "string" ? was.title : null;
}

/** The prior due date an edit recorded, when the edit moved it. */
export function priorDue(change: TodoChangeDTO | null | undefined): string | null {
  const was = (change?.detail as { was?: { due_at?: string | null } } | null | undefined)?.was;
  return change?.change === "edited" && was && "due_at" in was ? (was.due_at ?? null) : null;
}

/** What an open card shows of itself, whatever it proposes: Kory's why, the body the tap would use,
 *  who it goes to or when it holds, and what the tap does (only when the card runs something). */
export interface CardPreviewModel {
  why: string | null;
  body: string | null;
  to: string | null;
  when: string | null;
  consequence: string | null;
}

/** The proposed tool's argument that is the card's body (``kory.<tool>`` slugs). */
const BODY_ARGS: Record<string, readonly string[]> = {
  mail_create_draft: ["body"],
  props_give: ["message"],
  post_to_slack: ["text"],
  submit_it_request: ["description", "summary"],
  gdocs_create: ["content"],
  write_artifact: ["content"],
  remember: ["fact"],
  meeting_create_todo: ["notes"],
};

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
// A proposal's recipients are a list or a comma-separated string.
const recipients = (v: unknown) => (Array.isArray(v) ? str(v.map((t) => String(t).trim()).filter(Boolean).join(", ")) : str(v));

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** A hold's start or end, read the way the calendar books it: an offset time as given, a bare time on
 *  Eastern's wall clock, a bare date as the start (or, for ``end``, the end) of that Eastern day. */
function holdInstant(value: unknown, end = false): Date | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (DATE_ONLY.test(raw)) return combineBusinessDateTime(raw, end ? "23:59" : "00:00");
  const bare = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(:\d{2}(\.\d+)?)?$/.exec(raw);
  const instant = bare ? combineBusinessDateTime(bare[1], bare[2]) : parseISO(raw);
  return instant && isValid(instant) ? instant : null;
}

/** A hold given as dates only covers whole Eastern days. */
function allDay(start: unknown, end: unknown): boolean {
  return typeof start === "string" && typeof end === "string" && DATE_ONLY.test(start.trim()) && DATE_ONLY.test(end.trim());
}

const EASTERN_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: BUSINESS_TZ,
  weekday: "short",
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hourCycle: "h12",
});

/** An instant in Eastern: its day, weekday, "Oct 5", "1:00" and "PM". */
function eastern(instant: Date) {
  const parts = EASTERN_PARTS.formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    day: `${get("year")}-${get("month")}-${get("day")}`,
    weekday: get("weekday"),
    date: `${get("month")} ${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
    period: get("dayPeriod").toUpperCase(),
  };
}

function holdWhen(start: unknown, end: unknown): string | null {
  const a = holdInstant(start);
  if (!a) return null;
  const from = eastern(a);
  const b = holdInstant(end, true);
  if (b && allDay(start, end)) {
    const to = eastern(b);
    return `${from.weekday} ${from.date}${to.day === from.day ? "" : `–${to.weekday} ${to.date}`}, all day`;
  }
  const head = `${from.weekday} ${from.date}, ${from.time} ${from.period}`;
  if (!b) return head;
  const to = eastern(b);
  return `${head}–${to.day === from.day ? "" : `${to.weekday} ${to.date}, `}${to.time} ${to.period}`;
}

/** A hold's time for its button, in Eastern: "1:00–1:45 PM" today, "Thu 1:00–1:45 PM" on another day,
 *  "Fri 11:00 PM–Sat 1:00 AM" overnight, "Thu, all day" for dates only. Null without a valid start
 *  before its end. */
export function holdSpan(start: unknown, end: unknown, now: Date = new Date()): string | null {
  const a = holdInstant(start);
  const b = holdInstant(end, true);
  if (!a || !b || b <= a) return null;
  const from = eastern(a);
  const to = eastern(b);
  const today = eastern(now).day;
  if (allDay(start, end)) {
    return `${from.day === to.day ? (from.day === today ? "today" : from.weekday) : `${from.weekday}–${to.weekday}`}, all day`;
  }
  if (from.day !== to.day) return `${from.weekday} ${from.time} ${from.period}–${to.weekday} ${to.time} ${to.period}`;
  const day = from.day === today ? "" : `${from.weekday} `;
  return `${day}${from.period === to.period ? from.time : `${from.time} ${from.period}`}–${to.time} ${to.period}`;
}

/** The Kory call a card proposes: its action, the tool it names (``kory.<tool>``) and that call's params. */
export function koryAction(card: Pick<TodoDTO, "actions">): { action: TodoActionDTO | null; tool: string | null; params: Record<string, unknown> } {
  const action = card.actions?.find((a) => a.kind === "action" && a.action_slug?.startsWith("kory.")) ?? null;
  return { action, tool: action?.action_slug?.slice("kory.".length) ?? null, params: action?.params ?? {} };
}

/** The open card's parts, read only from what the card stores: ``subtitle`` is the why, then (after a
 *  blank line) the notes of a card that runs nothing, such as a ticket's body; a card that runs something
 *  carries its body in the proposed call's arguments and its consequence in the action's text. */
export function cardPreview(card: Pick<TodoDTO, "subtitle" | "actions">): CardPreviewModel {
  const [head, ...rest] = (card.subtitle ?? "").split(/\n\s*\n/);
  const notes = str(rest.join("\n\n"));
  const { action, tool, params } = koryAction(card);
  const body = tool ? ((BODY_ARGS[tool] ?? []).map((k) => str(params[k])).find(Boolean) ?? notes) : notes;
  return {
    why: str(head),
    body,
    to: tool === "mail_create_draft" ? recipients(params.to) : null,
    when: tool === "calendar_create_hold" ? holdWhen(params.start, params.end) : null,
    consequence: str(action?.content?.[0]),
  };
}

/** The tool a ``propose_*`` call stages, in the stored card's terms; null for a to-do that runs nothing. */
export function proposedTool(proposer: string, fields: Readonly<Record<string, string>>): string | null {
  if (proposer === "propose_email") return "mail_create_draft";
  if (proposer === "propose_hold") return "calendar_create_hold";
  if (proposer === "propose_props") return "props_give";
  if (proposer === "propose_action") return fields.tool || null;
  return proposer === "propose_todo" && fields.team_id ? "meeting_create_todo" : null;
}

/** A card still being proposed, previewed from its call's arguments so far, and from the card as stored
 *  once the result carries it. */
export function previewFromArgs(
  proposer: string,
  fields: Readonly<Record<string, string>>,
  stored?: { subtitle?: string; effect?: string | null },
): CardPreviewModel {
  const tool = proposedTool(proposer, fields);
  const subtitle = stored?.subtitle ?? `${fields.why ?? ""}${!tool && fields.notes ? `\n\n${fields.notes}` : ""}`;
  const actions = tool
    ? [{ kind: "action", action_slug: `kory.${tool}`, params: fields, content: stored?.effect ? [stored.effect] : [] }]
    : [];
  return cardPreview({ subtitle, actions });
}
